import { useMemo, useState } from 'react'
import { View, Text, Pressable, StyleSheet } from 'react-native'
import { useTranslation } from 'react-i18next'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { useAvatars } from '../../lib/useAvatars'
import { useRivalries, MAX_CHALLENGES } from '../../lib/useRivalries'
import { showConfirm } from '../../lib/platform'
import { Card, SectionLabel } from '../ui'
import { Msg } from '../Leagues/ui'
import MemberPicker from '../Leagues/MemberPicker'
import GradientButton from '../GradientButton'
import { ListSkeleton, ErrorState } from '../StateViews'
import PersonAvatarFace from '../PersonAvatarFace'
import RivalryCard from './RivalryCard'
import RivalrySheet from './RivalrySheet'

// Scheda "Sfide" della lega: lanci una sfida (massimo 3), rispondi a chi ti sfida, guardi quelle di tutti.
// members: [{ id_user, display_name }] (fn_league_members)
export default function RivalrySection({ leagueId, userId, members }) {
  const tc = useThemeColors()
  const { t } = useTranslation()
  const rv = useRivalries(leagueId, userId)
  const [notice, setNotice] = useState(null)
  const [open, setOpen] = useState(null)
  const [showEnded, setShowEnded] = useState(false)

  const ids = useMemo(() => [...new Set([...rv.rows.flatMap(r => [r.id_challenger, r.id_rival]), ...members.map(m => m.id_user)])], [rv.rows, members])
  const avatars = useAvatars(ids)

  const candidates = useMemo(
    () => members.filter(m => m.id_user !== userId && !rv.busyWith.has(m.id_user)).map(m => ({ out_id_user: m.id_user, out_display_name: m.display_name ?? '—' })),
    [members, userId, rv.busyWith],
  )

  const say = (type, text) => setNotice({ type, text })
  const explain = (res) => say('error', t(`rivalry.err_${res?.error === 'limit' || res?.error === 'exists' || res?.error === 'invalid' ? res.error : 'generic'}`))

  const challenge = async (targetId) => {
    const name = members.find(m => m.id_user === targetId)?.display_name ?? '—'
    if (!await showConfirm(t('rivalry.confirm_challenge', { name }))) return false
    const res = await rv.request(targetId)
    if (res.ok) { say('success', t('rivalry.sent', { name })); return true }
    explain(res); return false
  }
  const respond = async (r, accept) => {
    const res = await rv.respond(r.id, accept)
    if (!res.ok) explain(res)
    else if (accept) say('success', t('rivalry.accepted', { name: r.challenger_name }))
  }
  const cancel = async (r) => {
    const res = await rv.end(r.id)
    if (!res.ok) explain(res)
  }

  if (rv.loading && rv.rows.length === 0) return <ListSkeleton rows={3} />
  if (rv.error && rv.rows.length === 0) return <ErrorState onRetry={rv.reload} message={t('rivalry.load_error')} />

  const mineActive = rv.active.filter(rv.involved)
  const others = rv.active.filter(r => !rv.involved(r))
  const noneAtAll = rv.rows.length === 0

  const Person = ({ id, name }) => (
    <View style={s.person}>
      <PersonAvatarFace userId={id} settings={avatars[id]} size={36} />
      <Text style={[s.personName, { color: tc.text }]} numberOfLines={1}>{name}</Text>
    </View>
  )

  return (
    <View style={{ gap: 16, paddingHorizontal: 16 }}>
      {notice && <Msg type={notice.type} text={notice.text} />}

      <Card style={{ marginBottom: 0 }}>
        <SectionLabel>{t('rivalry.title')}</SectionLabel>
        <Text style={[s.body, { color: tc.textMuted }]}>{t('rivalry.intro')}</Text>
        <Text style={[s.slots, { color: tc.text }]}>{t('rivalry.slots', { used: rv.used, max: MAX_CHALLENGES })}</Text>
        {rv.used >= MAX_CHALLENGES ? (
          <Text style={[s.body, { color: tc.textMuted, marginTop: 4 }]}>{t('rivalry.limit_reached')}</Text>
        ) : candidates.length === 0 ? (
          <Text style={[s.body, { color: tc.textMuted, marginTop: 4 }]}>{t('rivalry.no_candidates')}</Text>
        ) : (
          <MemberPicker members={candidates} value={null} onChange={challenge} placeholder={t('rivalry.challenge_cta')} title={t('rivalry.pick_title')} style={{ marginTop: 8 }} />
        )}
      </Card>

      {rv.incoming.length > 0 && (
        <View style={{ gap: 8 }}>
          <Text style={[s.head, { color: tc.text }]} accessibilityRole="header">{t('rivalry.incoming_title')}</Text>
          {rv.incoming.map(r => (
            <View key={r.id} style={[s.reqCard, { backgroundColor: tc.rosaSubtle, borderColor: tc.rosaBorder }]}>
              <Person id={r.id_challenger} name={t('rivalry.incoming_line', { name: r.challenger_name })} />
              <View style={s.reqActions}>
                <GradientButton onPress={() => respond(r, true)} size="sm" style={{ flex: 1 }}>{t('rivalry.accept')}</GradientButton>
                <Pressable onPress={() => respond(r, false)} accessibilityRole="button" style={[s.declineBtn, { borderColor: tc.border, backgroundColor: tc.bgCard2 }]}>
                  <Text style={[s.declineText, { color: tc.textMuted }]}>{t('rivalry.decline')}</Text>
                </Pressable>
              </View>
            </View>
          ))}
        </View>
      )}

      {rv.outgoing.length > 0 && (
        <View style={{ gap: 8 }}>
          <Text style={[s.head, { color: tc.text }]} accessibilityRole="header">{t('rivalry.waiting_title')}</Text>
          {rv.outgoing.map(r => (
            <View key={r.id} style={[s.waitRow, { backgroundColor: tc.bgCard, borderColor: tc.border }]}>
              <Person id={r.id_rival} name={t('rivalry.waiting_line', { name: r.rival_name })} />
              <Pressable onPress={() => cancel(r)} accessibilityRole="button" accessibilityLabel={`${t('rivalry.cancel')}: ${r.rival_name}`} style={s.cancelBtn}>
                <Text style={[s.declineText, { color: tc.rosaText }]}>{t('rivalry.cancel')}</Text>
              </Pressable>
            </View>
          ))}
        </View>
      )}

      {mineActive.length > 0 && (
        <View style={{ gap: 10 }}>
          <Text style={[s.head, { color: tc.text }]} accessibilityRole="header">{t('rivalry.mine_title')}</Text>
          {mineActive.map(r => <RivalryCard key={r.id} row={r} meId={userId} avatars={avatars} onPress={() => setOpen(r)} />)}
        </View>
      )}

      {others.length > 0 && (
        <View style={{ gap: 10 }}>
          <Text style={[s.head, { color: tc.text }]} accessibilityRole="header">{t('rivalry.league_title')}</Text>
          {others.map(r => <RivalryCard key={r.id} row={r} meId={userId} avatars={avatars} onPress={() => setOpen(r)} />)}
        </View>
      )}

      {noneAtAll && <Text style={[s.empty, { color: tc.textMuted }]}>{t('rivalry.empty')}</Text>}

      {rv.ended.length > 0 && (
        <View style={{ gap: 10 }}>
          <Pressable onPress={() => setShowEnded(v => !v)} accessibilityRole="button" accessibilityState={{ expanded: showEnded }} style={s.endedHead}>
            <Text style={[s.head, { color: tc.text }]}>{t('rivalry.ended_title', { count: rv.ended.length })}</Text>
            <MaterialCommunityIcons name={showEnded ? 'chevron-up' : 'chevron-down'} size={20} color={tc.textMuted} />
          </Pressable>
          {showEnded && rv.ended.map(r => <RivalryCard key={r.id} row={r} meId={userId} avatars={avatars} onPress={() => setOpen(r)} />)}
        </View>
      )}

      <RivalrySheet
        row={open}
        meId={userId}
        avatars={avatars}
        canRematch={rv.used < MAX_CHALLENGES}
        onClose={() => setOpen(null)}
        onEnd={rv.end}
        onRematch={challenge}
      />
    </View>
  )
}

const s = StyleSheet.create({
  body: { fontSize: 13, lineHeight: 19, marginTop: 6 },
  slots: { fontFamily: fonts.condensedBold, fontSize: 15, marginTop: 12 },
  head: { fontFamily: fonts.condensedBold, fontSize: 18 },
  person: { flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1 },
  personName: { flex: 1, fontSize: 14, fontFamily: fonts.condensedBold },
  reqCard: { borderWidth: 1, borderRadius: 14, padding: 12, gap: 10 },
  reqActions: { flexDirection: 'row', gap: 8 },
  declineBtn: { flex: 1, minHeight: 44, borderWidth: 1, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  declineText: { fontFamily: fonts.condensedBold, fontSize: 13 },
  waitRow: { flexDirection: 'row', alignItems: 'center', gap: 8, borderWidth: 1, borderRadius: 14, paddingLeft: 12, paddingVertical: 6 },
  cancelBtn: { minHeight: 44, paddingHorizontal: 14, justifyContent: 'center' },
  empty: { fontSize: 14, textAlign: 'center', paddingVertical: 12 },
  endedHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', minHeight: 44 },
})
